import React, { useRef, useState } from 'react';
import recallService from '../../../../services/recall.service';
import { showError, showSuccess } from '../../../../utils/ecxSwal';
import { isAllowedRecallFile, RECALL_ALLOWED_EXTENSIONS } from './recallsQuery';

function RecallCreatePanel({ clinicId, onCreated, disabled = false }) {
    const fileInputRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [title, setTitle] = useState('');
    const [notes, setNotes] = useState('');
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const resetForm = () => {
        setTitle('');
        setNotes('');
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (event) => {
        const nextFile = event.target.files?.[0];

        if (!nextFile) {
            setFile(null);
            return;
        }

        if (!isAllowedRecallFile(nextFile)) {
            showError({
                title: 'Invalid file',
                text: `Allowed file types: ${RECALL_ALLOWED_EXTENSIONS.join(', ')}`,
            });
            event.target.value = '';
            setFile(null);
            return;
        }

        setFile(nextFile);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const trimmedTitle = title.trim();

        if (!clinicId) {
            return;
        }

        if (trimmedTitle.length < 4) {
            showError({
                title: 'Title required',
                text: 'Please enter a title with at least 4 characters.',
            });
            return;
        }

        setSubmitting(true);

        try {
            const response = await recallService.createRecallRequest(clinicId, {
                title: trimmedTitle,
                notes: notes.trim() || undefined,
            });

            const recallId = response.data?.id;

            if (file && recallId) {
                await recallService.uploadRecallFiles(clinicId, recallId, file);
            }

            showSuccess({
                title: 'Recall submitted',
                text: file
                    ? 'Your recall request was created and the file was uploaded.'
                    : 'Your recall request was created. You can upload a file from the list.',
            });

            resetForm();
            setExpanded(false);
            onCreated?.();
        } catch (error) {
            console.error('Failed to create recall request', error);
            showError({
                title: 'Submit failed',
                text: error?.response?.data?.message || 'Unable to create recall request. Please try again.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={`recall-create-panel${expanded ? ' recall-create-panel--expanded' : ''}`}>
            <div className="recall-create-panel__header">
                <div>
                    <h5 className="mb-1 fw-normal text-dark">New recall request</h5>
                    <p className="mb-0 small text-muted">
                        Submit a recall batch and upload your recall file for processing.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={() => setExpanded((open) => !open)}
                    disabled={disabled}
                >
                    <i className={`bi bi-${expanded ? 'dash-lg' : 'plus-lg'} me-1`}></i>
                    {expanded ? 'Close' : 'New request'}
                </button>
            </div>

            {expanded && (
                <form className="recall-create-panel__body" onSubmit={handleSubmit}>
                    <div className="row g-3">
                        <div className="col-12 col-lg-6">
                            <label className="form-label small fw-normal text-muted">Title</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. March 2026 recall batch"
                                value={title}
                                onChange={(event) => setTitle(event.target.value)}
                                disabled={submitting || disabled}
                                maxLength={120}
                                required
                            />
                        </div>

                        <div className="col-12 col-lg-6">
                            <label className="form-label small fw-normal text-muted">Recall file</label>
                            <div className="recall-file-input">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="recall-file-input__native"
                                    accept=".csv,.doc,.docx,.pdf"
                                    onChange={handleFileChange}
                                    disabled={submitting || disabled}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm rounded-pill"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={submitting || disabled}
                                >
                                    <i className="bi bi-upload me-1"></i>
                                    Choose file
                                </button>
                                <span className="recall-file-input__name text-muted small">
                                    {file ? file.name : `Allowed: ${RECALL_ALLOWED_EXTENSIONS.join(', ')}`}
                                </span>
                            </div>
                        </div>

                        <div className="col-12">
                            <label className="form-label small fw-normal text-muted">Notes</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="Optional notes for the EyeconX team..."
                                value={notes}
                                onChange={(event) => setNotes(event.target.value)}
                                disabled={submitting || disabled}
                            />
                        </div>
                    </div>

                    <div className="recall-create-panel__actions">
                        <button
                            type="button"
                            className="btn btn-light btn-sm rounded-pill"
                            onClick={resetForm}
                            disabled={submitting}
                        >
                            Clear
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary btn-sm rounded-pill"
                            disabled={submitting || disabled}
                        >
                            {submitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-send me-1"></i>
                                    Submit recall request
                                </>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

export default RecallCreatePanel;
